<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import GroupsAPI from 'dashboard/api/masifiWhatsappQrGroups';
import MasifiWaContactGroups from './MasifiWaContactGroups.vue';
import {
  isMasifiWaGroupContact,
  masifiWaLastActivityId,
} from 'dashboard/helper/masifiWaGroups';
import { WHATSAPP_QR_FEATURE } from 'dashboard/helper/masifiWhatsappQr';

// Panel del grupo de WhatsApp QR (spec F3 §6.1): nombre, descripción, tamaño, estado de NUESTRO número, participantes
// paginados (admins primero; insignias Admin/Equipo/Este número) y, para el admin (F3a), modo CRM, "Es de mi equipo",
// número que atiende (con 2+) y "Actualizar". Montado en ContactPanel.vue (import + 1 línea). Para una PERSONA, sus
// "Grupos de WhatsApp" (MasifiWaContactGroups, E2E fix batch 1): mismo montaje, 0 touchpoints core nuevos.
const props = defineProps({
  contact: { type: Object, default: () => ({}) },
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const { isAdmin } = useAdmin();
const { accountId, isCloudFeatureEnabled } = useAccount();
const router = useRouter();
const currentChat = useMapGetter('getSelectedChat');

const isGroup = computed(
  () =>
    isMasifiWaGroupContact(props.contact) &&
    isCloudFeatureEnabled(WHATSAPP_QR_FEATURE)
);
const isOpen = ref(true);
const group = ref(null);
const members = ref([]);
const total = ref(0);
const page = ref(1);
const query = ref('');

const fetchMembers = async (nextPage = 1) => {
  const { data } = await GroupsAPI.members(props.conversationId, {
    page: nextPage,
    q: query.value,
  });
  members.value =
    nextPage === 1 ? data.payload : [...members.value, ...data.payload];
  total.value = data.meta.total;
  page.value = nextPage;
};

const load = async () => {
  if (!isGroup.value) return;
  try {
    const { data } = await GroupsAPI.get(props.conversationId);
    group.value = data;
    await fetchMembers(1);
  } catch (error) {
    group.value = null;
  }
};

watch(() => [props.conversationId, isGroup.value], load, { immediate: true });
watch(
  query,
  debounce(() => fetchMembers(1).catch(() => {}), 300)
);
// Ruling 22: una línea de actividad nueva en el hilo abierto = algo cambió en el grupo.
watch(
  () => masifiWaLastActivityId(currentChat.value?.messages),
  (id, previous) => id && previous && id !== previous && load()
);

const hasMore = computed(() => members.value.length < total.value);
const canManage = computed(() => isAdmin.value && group.value?.can_manage);
const crmOptions = computed(() =>
  ['account_default', 'customer', 'off'].map(value => ({
    value,
    label: t(`MASIFI_WHATSAPP_QR.GROUPS.PANEL.CRM_MODE.${value.toUpperCase()}`),
  }))
);
// E2E fix batch 5 (item 4, decisión de Marcos): con el CRM del grupo apagado, un lead que ya tenía se mantiene; el
// servidor manda `lead` solo en ese caso ({ id: null } = el que mira no puede ver ese lead → aviso sin enlace).
const keptLead = computed(() => group.value?.lead || null);
const openKeptLead = () =>
  router.push({
    name: 'leads_show',
    params: { accountId: accountId.value, leadId: keptLead.value.id },
  });
const numberOptions = computed(() =>
  (group.value?.numbers || []).map(number => ({
    value: number.inbox_id,
    label: number.name,
  }))
);
const roleLabel = role =>
  t(`MASIFI_WHATSAPP_QR.GROUPS.PANEL.ROLE.${(role || 'member').toUpperCase()}`);
const isAdminRole = role => ['admin', 'superadmin'].includes(role);

// Final review A-m2: our errors carry a `code` and a server-side es/en text; the native 401/403/404 bodies are English
// → our own text for those, the generic one for anything else.
const NATIVE_ERRORS = {
  401: 'UNAUTHORIZED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
};
const fail = error => {
  const { status, data } = error?.response || {};
  if (data?.code && data?.error) {
    useAlert(data.error);
    return;
  }
  const key = NATIVE_ERRORS[status];
  useAlert(
    t(
      key
        ? `MASIFI_WHATSAPP_QR.GROUPS.PANEL.ERRORS.${key}`
        : 'MASIFI_WHATSAPP_QR.GROUPS.PANEL.SAVE_ERROR'
    )
  );
};

const save = async attributes => {
  try {
    const { data } = await GroupsAPI.update(props.conversationId, attributes);
    group.value = data;
    useAlert(t('MASIFI_WHATSAPP_QR.GROUPS.PANEL.SAVED'));
  } catch (error) {
    fail(error);
  }
};

const refresh = async () => {
  try {
    await GroupsAPI.sync(props.conversationId);
    useAlert(t('MASIFI_WHATSAPP_QR.GROUPS.PANEL.REFRESH_QUEUED'));
  } catch (error) {
    fail(error);
  }
};

const toggleTeam = async member => {
  try {
    const { data } = member.team
      ? await GroupsAPI.unmarkTeam(props.conversationId, member.id)
      : await GroupsAPI.markTeam(props.conversationId, member.id);
    members.value = members.value.map(item =>
      item.id === data.id ? data : item
    );
  } catch (error) {
    fail(error);
  }
};
</script>

<template>
  <div v-if="isGroup && group" class="px-2 pb-2">
    <AccordionItem
      :title="t('MASIFI_WHATSAPP_QR.GROUPS.PANEL.TITLE')"
      :is-open="isOpen"
      @toggle="isOpen = !isOpen"
    >
      <div class="flex flex-col gap-3 text-sm">
        <p v-if="group.description" class="text-n-slate-12 whitespace-pre-line">
          {{ group.description }}
        </p>
        <p class="text-n-slate-11">
          {{
            t('MASIFI_WHATSAPP_QR.GROUPS.PANEL.SIZE', {
              count: group.size || total,
            })
          }}
        </p>
        <div class="flex flex-wrap items-center gap-2">
          <span class="text-n-slate-11">
            {{ t('MASIFI_WHATSAPP_QR.GROUPS.PANEL.OUR_NUMBER') }}
          </span>
          <Label
            v-if="group.our_number.left"
            color="ruby"
            :label="t('MASIFI_WHATSAPP_QR.GROUPS.PANEL.LEFT')"
            compact
          />
          <Label v-else :label="roleLabel(group.our_number.role)" compact />
          <Label
            v-if="!group.our_number.groups_enabled"
            color="amber"
            :label="t('MASIFI_WHATSAPP_QR.GROUPS.PANEL.GROUPS_OFF')"
            compact
          />
        </div>

        <template v-if="canManage">
          <label class="flex flex-col gap-1">
            <span class="text-n-slate-11">
              {{ t('MASIFI_WHATSAPP_QR.GROUPS.PANEL.CRM_MODE.LABEL') }}
            </span>
            <Select
              :model-value="group.crm_mode"
              :options="crmOptions"
              @update:model-value="value => save({ crmMode: value })"
            />
          </label>
          <Banner
            v-if="keptLead"
            color="amber"
            :action-label="
              keptLead.id
                ? t('MASIFI_WHATSAPP_QR.GROUPS.PANEL.CRM_MODE.OPEN_LEAD')
                : null
            "
            @action="openKeptLead"
          >
            {{ t('MASIFI_WHATSAPP_QR.GROUPS.PANEL.CRM_MODE.KEPT_LEAD') }}
          </Banner>
          <label v-if="numberOptions.length > 1" class="flex flex-col gap-1">
            <span class="text-n-slate-11">
              {{ t('MASIFI_WHATSAPP_QR.GROUPS.PANEL.HANDLING.LABEL') }}
            </span>
            <Select
              :model-value="group.handling_inbox_id"
              :options="numberOptions"
              @update:model-value="
                value => save({ handlingInboxId: Number(value) })
              "
            />
          </label>
          <div>
            <Button
              slate
              faded
              sm
              icon="i-lucide-refresh-cw"
              :label="t('MASIFI_WHATSAPP_QR.GROUPS.PANEL.REFRESH')"
              @click="refresh"
            />
          </div>
        </template>

        <Input
          v-model="query"
          :placeholder="t('MASIFI_WHATSAPP_QR.GROUPS.PANEL.SEARCH')"
        />
        <ul class="flex flex-col gap-2">
          <li
            v-for="member in members"
            :key="member.id"
            class="flex items-center gap-2 min-w-0"
          >
            <Avatar
              :name="member.name"
              :src="member.thumbnail || ''"
              :size="24"
            />
            <span class="truncate text-n-slate-12">{{ member.name }}</span>
            <Label
              v-if="isAdminRole(member.role)"
              color="blue"
              :label="roleLabel(member.role)"
              compact
            />
            <Label
              v-if="member.team"
              color="teal"
              :label="t('MASIFI_WHATSAPP_QR.GROUPS.PANEL.BADGE_TEAM')"
              compact
            />
            <Label
              v-if="member.this_number"
              color="iris"
              :label="t('MASIFI_WHATSAPP_QR.GROUPS.PANEL.BADGE_THIS_NUMBER')"
              compact
            />
            <Button
              v-if="canManage && !member.our_number"
              :data-test="`masifi-wa-team-${member.id}`"
              link
              xs
              slate
              class="ltr:ml-auto rtl:mr-auto"
              :label="
                t(
                  member.team
                    ? 'MASIFI_WHATSAPP_QR.GROUPS.PANEL.TEAM.UNMARK'
                    : 'MASIFI_WHATSAPP_QR.GROUPS.PANEL.TEAM.MARK'
                )
              "
              @click="toggleTeam(member)"
            />
          </li>
        </ul>
        <div v-if="hasMore">
          <Button
            link
            sm
            :label="t('MASIFI_WHATSAPP_QR.GROUPS.PANEL.LOAD_MORE')"
            @click="fetchMembers(page + 1)"
          />
        </div>
      </div>
    </AccordionItem>
  </div>
  <MasifiWaContactGroups
    v-else-if="!isMasifiWaGroupContact(contact)"
    :contact="contact"
    :conversation-id="conversationId"
  />
</template>
